import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../utils/Api';

const ProductPage = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const data = await apiFetch(`/api/product/${id}`);
        setCar(data);
        if (data.imageLinks && data.imageLinks.length > 0) {
          setSelectedImage(data.imageLinks[0]);
        }
      } catch (error) {
        console.error('Error fetching car data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const nextImage = () => {
    if (car?.imageLinks?.length) {
      setActiveImageIndex((prev) => (prev + 1) % car.imageLinks.length);
    }
  };

  const previousImage = () => {
    if (car?.imageLinks?.length) {
      setActiveImageIndex((prev) => (prev - 1 + car.imageLinks.length) % car.imageLinks.length);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isModalOpen) return;

      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') previousImage();
      if (e.key === 'Escape') setIsModalOpen(false);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isModalOpen, car?.imageLinks?.length]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <h2 className="text-2xl text-gray-600">Auto niet gevonden</h2>
      </div>
    );
  }

  const specifications = [
    { label: 'Kenteken', value: car.kenteken },
    { label: 'Transmissie', value: car.transmissie },
    { label: 'Brandstof', value: car.brandstof },
    { label: 'BTW', value: car.inOfEXBTW },
    { label: 'Kilometerstand', value: `${car.kmstand?.toLocaleString()} km` },
    { label: 'Bouwjaar', value: car.datum },
    { label: 'Vermogen', value: `${car.pk} PK` },
    { label: 'Gewicht', value: `${car.gewicht} kg` },
    { label: 'Aantal deuren', value: car.aantalDeuren },
    { label: 'Aantal zitplaatsen', value: car.aantalzitplaatsen },
    { label: 'Aantal cilinders', value: car.aantalCilinders },
    { label: 'Motorinhoud', value: `${car.motorinhoud} cc` },
    { label: 'Carrosserie', value: car.carrosserie },
    { label: 'Kleur', value: car.kleur },
    { label: 'Interieurkleur', value: car.interieurkleur },
    { label: 'Bekledingsoort', value: car.bekledingsoort },
    { label: 'Aandrijving', value: car.aandrijving },
    { label: 'APK tot', value: car.apk }
  ];



  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-4 lg:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-3 mb-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{car.merk} {car.model}</h1>
              <p className="text-gray-600 mt-1">{car.uitvoering}</p>
              <p className="text-gray-500 mt-2">{car.kenteken}</p>
              {car.verkocht && (
                <div className="mt-2 inline-block bg-red-600 text-white font-bold py-1 px-3 rounded">
                  VERKOCHT
                </div>
              )}
            </div>
            <div className="mt-4 md:mt-0">
              <div className="text-2xl font-bold text-blue-600">{formatPrice(car.prijs)}</div>
              <div className="text-base font-medium text-gray-800 mt-1">
                {car.inOfEXBTW === "Marge"
                  ? 'Marge'
                  : car.inOfEXBTW
                    ? `${car.inOfEXBTW} BTW`
                    : 'BTW status niet beschikbaar'}
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="md:col-span-3">
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="aspect-w-16 aspect-h-9 mb-4">
                    <div
                      className="w-full h-[728.5px] bg-gray-100 rounded-lg overflow-hidden cursor-pointer"
                      onClick={() => setIsModalOpen(true)}
                    >
                      <img
                        src={`https://s-backend-rebuild-production.up.railway.app/${car?.imageLinks?.[activeImageIndex]?.replace(/^\/+/, '')}`}
                        alt={`${car?.merk} ${car?.model}`}
                        className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                      />
                    </div>
                  </div>

                  {car?.imageLinks && car.imageLinks.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newIndex = activeImageIndex === 0
                            ? car.imageLinks.length - 1
                            : activeImageIndex - 1;
                          setActiveImageIndex(newIndex);
                        }}
                        className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      <div className="grid grid-cols-5 gap-1 flex-1">
                        {car.imageLinks.slice(Math.floor(activeImageIndex / 5) * 5, Math.floor(activeImageIndex / 5) * 5 + 5).map((imageUrl, index) => (
                          <div
                            key={index + Math.floor(activeImageIndex / 5) * 5}
                            onClick={() => setActiveImageIndex(index + Math.floor(activeImageIndex / 5) * 5)}
                            className={`
                              relative aspect-square cursor-pointer rounded-lg overflow-hidden
                              transition-all duration-200 hover:opacity-90
                              ${(index + Math.floor(activeImageIndex / 5) * 5) === activeImageIndex
                                ? 'ring-10 ring-blue-500 shadow-lg'
                                : 'hover:ring-5 hover:ring-blue-300'}
                            `}
                          >
                            <img
                              src={`https://s-backend-rebuild-production.up.railway.app/${imageUrl.replace(/^\/+/, '')}`}
                              alt={`${car.merk} ${car.model} thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newIndex = activeImageIndex === car.imageLinks.length - 1
                            ? 0
                            : activeImageIndex + 1;
                          setActiveImageIndex(newIndex);
                        }}
                        className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="md:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="space-y-4">
                  <div className="text-2xl font-bold text-gray-900">
                    {car.verkocht ? 'Deze auto is verkocht' : 'Interesse in deze auto?'}
                  </div>
                  {!car.verkocht ? (
                    <Link
                      to="/contact"
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg text-sm transition duration-300 ease-in-out transform hover:scale-105 shadow-lg w-full text-center block flex items-center justify-center gap-2 no-underline"
                    >
                      <span>Neem contact op</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  ) : (
                    <div className="bg-gray-400 text-white font-bold py-2 px-6 rounded-lg text-sm w-full text-center">
                      Deze auto is niet meer beschikbaar
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-semibold mb-6 pb-2 border-b">Specificaties</h2>
                <div className="grid grid-cols-1 gap-y-3">
                  {specifications.map(({ label, value }) => value && (
                    <div key={label} className="flex justify-between items-center">
                      <span className="text-gray-600">{label}</span>
                      <span className="font-medium text-gray-900 ml-4">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {car.omschrijving && (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
              <h2 className="text-2xl font-semibold mb-4 pb-2 border-b">Omschrijving</h2>
              <p className="text-gray-700 whitespace-pre-line leading-relaxed">{car.omschrijving}</p>
            </div>
          )}

          {car.opties && (
            <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
              <h2 className="text-2xl font-semibold mb-4 pb-2 border-b">Opties</h2>
              <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                {car.opties.split(',').map((option, index) => (
                  <div key={index} className="flex items-center break-inside-avoid mb-2">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{option.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setIsModalOpen(false)}>
          <div className="relative w-full h-full flex items-center justify-center p-4">
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
              onClick={() => setIsModalOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              className="absolute left-4 text-white hover:text-gray-300 z-50"
              onClick={(e) => {
                e.stopPropagation();
                previousImage();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              className="absolute right-4 text-white hover:text-gray-300 z-50"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <img
              src={`https://s-backend-rebuild-production.up.railway.app/${car?.imageLinks?.[activeImageIndex]?.replace(/^\/+/, '')}`}
              alt={`${car?.merk} ${car?.model}`}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white">
              {activeImageIndex + 1} / {car?.imageLinks?.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;